import { useEffect, useRef } from 'react';

/**
 * Calls a callback if a user clicks outside of the element(s) passed
 * @param {Object|Array} refOrRefs Ref or array of refs. Only accepts object refs
 * @param {Function} fn Callback function
 * @param {Boolean} cond Whether it's active or not
 */
function useOnOutsideClick(refOrRefs, fn, cond = true) {
  const refs = Array.isArray(refOrRefs) ? refOrRefs : [refOrRefs];
  const isTouchMove = useRef(false);

  function handleClick(event) {
    const hasTargetWithin = refs
      .filter((ref) => ref.current)
      .some((ref) => isTargetWithin(event.target, ref.current, true));
    if (cond && !hasTargetWithin) {
      fn();
    }
  }
  function handleTouchStart() {
    isTouchMove.current = false;
  }

  function handleTouchMove() {
    isTouchMove.current = true;
  }

  function handleTouchEnd(event) {
    if (
      !isTouchMove.current &&
      cond &&
      refs.every((ref) => !isTargetWithin(event.target, ref.current, true, false))
    ) {
      // To prevent immediately clicking on an element behind an element
      // that disappears after calling the callback
      setTimeout(fn);
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleClick, true);
    document.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  });
}

function isOrContainsNode(parent, child) {
  return parent === child || containsNode(parent, child);
}

function containsNode(parent, child) {
  return parent.contains(child);
}

function isTargetWithin(eventTarget, parent, includeParent = false, checkActiveElement = true) {
  const checkFunc = includeParent ? isOrContainsNode : containsNode;
  return (
    checkFunc(parent, eventTarget) ||
    (checkActiveElement && checkFunc(parent, document.activeElement))
  );
}

export default useOnOutsideClick;
export { isTargetWithin };
